import {
	IDictionary,
	Action
} from 'util/domain';
import { CommonUtilsService } from 'util/services/commonUtilsService';

interface EventCallback<P> {
	(args:P, event?: ng.IAngularEvent, context?:string): void;
}

type SubscriptionTuple<P> = [string, EventCallback<P>];
type ActionRegistry = IDictionary<IDictionary<EventCallback<any>>>;

interface Event<P> {
	readonly type:string;
	readonly payload?:P;
}

/**
* @ngdoc service
* @name  phoenix.pxActionService
* 
* @description 
* A service to register and raise actions
* 
**/
export class ActionService {
	public static $inject = [
		'utils',
		'$q'
	];

	private subscriptions:ActionRegistry = {};

	constructor(
		private readonly utils:CommonUtilsService,
		private readonly $q:ng.IQService
	){
	};

	/**
	* @ngdoc method
	* @name pxActionService#emit
	* @methodOf phoenix.pxActionService
	* @kind function
	*
	* @description
	* Raises an action event.
	* 
	* @param {string} name  The name of action to raise.
	* @param {object} event The event that raised action.
	* @param {string} context User-defined context with which to disambiguate in listeners.
	*
	*/
	public emit<P>(action:Event<P>, event:ng.IAngularEvent = null, context?:string){
		if(!action || !action.type){
			throw new Error('Invalid action specified.');
		}

		let type = action.type.toLowerCase();
		let results = [];
		if(type in this.subscriptions){          
			let subs = this.subscriptions[type];
			results = Object.keys(subs)
				.map(key => subs[key](action.payload, event, context));
		}

		return this.$q.all<any>(results);
	}

	/**
	* @ngdoc method
	* @name pxActionService#subscribe
	* @methodOf phoenix.pxActionService
	* @kind function
	*
	* @description
	* Subscribes to an action.
	* 
	* @param {string} name  The name of action to subscribe to.
	* @param {function} callback The function to call on subscription trigger.
	*
	*/
	public subscribe<P>(subscriptions:SubscriptionTuple<P>[]): Action[];
	public subscribe<P>(actionName:string, callback:EventCallback<P>): Action;
	public subscribe<P>(actionNameOrTuples:string|SubscriptionTuple<P>[], callback?:EventCallback<P>){
		let tuples:SubscriptionTuple<P>[];
		let unsubs:Action[] = [];

		if(Array.isArray(actionNameOrTuples)){
			tuples = actionNameOrTuples;
		}
		else {
			tuples = [[actionNameOrTuples, callback]];
		}

		tuples.forEach(t => {
			let actionName = t[0].toLowerCase();
			callback = t[1];

			if(!this.subscriptions[actionName]){
				this.subscriptions[actionName] = {};
			}

			var token = this.utils.generateUid();
			this.subscriptions[actionName][token] = callback;

			unsubs.push(() => this.unsubscribe(actionName, token));
		});

		return unsubs.length === 1 ? unsubs[0] : unsubs;
	}

	private unsubscribe(actionName, token){
		this.subscriptions[actionName][token] = null;
		delete this.subscriptions[actionName][token];
	}
}



// WEBPACK FOOTER //
// ./src/phoenix/services/actionService.ts